import { useAuth } from '@clerk/clerk-react'
import { useEffect, useState } from 'react'

import { Pill } from '@/components/ui/Pill'
import { listAdminFeedback } from '@/lib/api/admin'
import { ApiError } from '@/lib/api/client'
import { cn } from '@/lib/cn'
import type { AdminFeedback } from '@/lib/types/admin'
import type { FeedbackTopic } from '@/lib/types/feedback'

const TOPIC_LABEL: Record<FeedbackTopic, string> = {
  bug: 'Bug',
  idea: 'Idea',
  other: 'Other',
}

function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function preview(message: string): string {
  const line = message.trim().split(/\n/)[0] ?? ''
  return line.length > 72 ? `${line.slice(0, 72)}…` : line
}

export function AdminInbox() {
  const { getToken } = useAuth()
  const [items, setItems] = useState<AdminFeedback[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [mobileDetail, setMobileDetail] = useState(false)

  const selected = items.find((item) => item.id === selectedId) ?? null

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listAdminFeedback(getToken)
      .then((data) => {
        if (cancelled) {
          return
        }
        setItems(data)
        setSelectedId((current) => {
          if (current && data.some((item) => item.id === current)) {
            return current
          }
          return data[0]?.id ?? null
        })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.detail : "Couldn't load support.",
          )
          setItems([])
          setSelectedId(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [getToken])

  if (loading) {
    return <p className="font-body text-body-md text-fg-muted">Loading…</p>
  }

  if (items.length === 0 && !error) {
    return (
      <p className="font-body text-body-md text-fg-muted">No messages yet.</p>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="font-body text-body-sm text-error" role="alert">
          {error}
        </p>
      )}
      <div className="border-glass-border grid min-h-[28rem] overflow-hidden rounded-lg border md:grid-cols-[minmax(16rem,20rem)_1fr]">
        <ul
          className={cn(
            'border-glass-border divide-y overflow-y-auto md:border-r',
            mobileDetail && 'hidden md:block',
          )}
        >
          {items.map((item) => {
            const active = item.id === selectedId
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(item.id)
                    setMobileDetail(true)
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left transition-colors',
                    active
                      ? 'bg-primary-container/15'
                      : 'hover:bg-white/5',
                  )}
                >
                  <p className="font-display text-fg truncate font-semibold">
                    {TOPIC_LABEL[item.topic]}
                  </p>
                  <p className="font-body text-body-sm text-fg-muted truncate">
                    {preview(item.message)}
                  </p>
                  <p className="font-body text-label-sm text-fg-muted mt-1">
                    {formatWhen(item.createdAt)}
                  </p>
                </button>
              </li>
            )
          })}
        </ul>

        <section
          className={cn(
            'flex flex-col gap-5 p-5',
            !mobileDetail && 'hidden md:flex',
          )}
        >
          {selected ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setMobileDetail(false)
                }}
                className="font-body text-label-sm text-primary hover:text-primary-fixed-dim self-start md:hidden"
              >
                Back to list
              </button>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="neutral">{TOPIC_LABEL[selected.topic]}</Pill>
                  <Pill tone="neutral">
                    {selected.userId ? 'Signed in' : 'Guest'}
                  </Pill>
                </div>
                <p className="font-body text-label-sm text-fg-muted">
                  {formatWhen(selected.createdAt)}
                </p>
              </div>

              <p className="font-body text-body-md text-fg whitespace-pre-wrap text-pretty">
                {selected.message}
              </p>

              <p className="font-body text-body-sm text-fg-muted break-all">
                {selected.email}
                {selected.path ? ` · ${selected.path}` : ''}
              </p>
            </>
          ) : (
            <p className="font-body text-body-md text-fg-muted">
              Select a message.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
